/* eslint-disable react-hooks/exhaustive-deps */

import React, { useEffect, useCallback, memo, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { Box, IconButton, Paper, Typography, CircularProgress, Tooltip } from '@mui/material';
import { MaterialReactTable, MRT_ColumnDef, MRT_Row } from 'material-react-table';
import ProvisionSelectorPopup from './popup/provision-selector-popup';
import ActionMenu from './action-menu';
import { DropdownOption } from './ui/dropdown';
import TreeDropdown from './tree-dropdown';
import AmountFilter from './filters/amount-filter';
import DescriptionFilter from './filters/description-filter';
import CategoryFilter from './filters/category-filter';
import ActionsFilter, { ActionIconType } from './filters/actions-filter';
import DateFilter from './filters/date-filter';
import BankFilter from './filters/bank-filter';
import { SlidersHorizontal, Link, SquareCheckBig, Atom, RefreshCw, CalendarClock, Ellipsis } from 'lucide-react';
import XeroIcon from './icons/xero-icon';
import TablePlaceholder from './table-placeholder';
import CategoryTaxConfirmPopup from './popup/category-tax-confirm';
import DeletePopup from './popup/delete';
import ProvisionTaxScopePopup from './popup/provision-tax-scope';
import { useSettings } from '../providers/settings-provider';
import { useUser } from '../providers/user-provider';
import { usePermissions } from '../providers/permissions-provider';
import { reformatGstDescriptionDates } from '../util/date';

export type TransactionRow = {
    id: string;
    date: string;
    amount: number | string;
    balance: number | string | null;
    // Already Xero-preferred when a Xero description overlay exists (see
    // applyXeroDescriptionOverlay in transaction-controller.js) - the stable
    // local/original value (used by dedup matching on repeat syncs) is
    // exposed separately as originalDescription.
    description: string;
    originalDescription?: string;
    bank: string;
    category: string;
    appliedTaxIds?: number[];
    type: 'real' | 'provision';
    provisionId?: number | null;
    staffPaymentId?: number | null;
    isRecurring?: boolean;
    isProvisioned?: boolean;
    invoiceNumber?: string | null;
    linkedTransactionId?: string | null;
    // Whether this row (real or provision) is on either side of a link -
    // a real transaction that's linked TO never has linkedTransactionId set
    // on itself, so this reverse-direction flag (computed server-side) is
    // what the "linked" icon actually needs to check.
    hasLinkedTransactions?: boolean;
    provisionThisTemplateId?: number | null;
    isSimulation?: boolean;
    // Invoice was already PAID in Xero when imported - render like a real
    // transaction even though type stays 'provision' (see xero-import-service.js).
    // Already false whenever hasLinkedTransactions is true (see transactions-virtual.tsx).
    paidAtImport?: boolean;
    // Category was overwritten (and/or a Xero description overlay exists)
    // during CSV<->Xero reconciliation (see xero-import-service.js) -
    // original/overridden values are in the detail popup.
    reconciledWithXero?: boolean;
    // This row is linked to a Xero record at all (independent of whether
    // reconciledWithXero's description/category override happened - e.g.
    // inter-account transfers get xeroTransactionId without any override,
    // since they carry no AccountCode to resolve a category from).
    xeroTransactionId?: string | null;
    // Set by the provision-aging nightly job (provision-aging-service.js,
    // action push_fixed/push_configurable) when it moves this provision's
    // date forward - drives the date-shifted-provision row highlight.
    pushedForwardForAging?: boolean;
    // True when this row's amount was excluded from the running balance walk
    // (pushedForwardForAging is true and the entity has "Include pushed-
    // forward provisions in the balance calculation" turned off) - the Cash
    // Position column shows "-" instead of its carried-over balance number,
    // since that number reflects the prior row, not this one.
    excludedFromBalance?: boolean;
    // Real transaction added by hand via the "+ Transaction" button - the
    // only kind that may be edited/deleted (imported/synced ones never are).
    // Enforced server-side too (sourceData.manual, transaction-controller.js).
    isManual?: boolean;
    // Set by updateProvisionDate (transaction-controller.js) whenever a
    // user manually changes a provision's date via "Change Date" - either
    // direction, forward or back. Drives the "moved back" row highlight,
    // which is really "this date isn't where it originally was" rather
    // than specifically "moved earlier".
    dateAdjustedBack?: boolean;
    // The manual "OPEN"/"CLOSED" toggle (transaction-detail.tsx) - false
    // means no more transactions/provisions can be linked to this one.
    // Auto-set false server-side when a provision becomes fully satisfied
    // (bulkUpdateLinkedTransactions, transaction-controller.js). Null/undefined
    // (a handful of legacy rows) is treated as closed, matching the detail
    // modal's own fallback.
    isLinkable?: boolean;
    // The natural-pattern/original-import date this occurrence had before
    // it was first pushed forward or manually adjusted - pinned to that
    // first move, never overwritten by later ones (transaction-controller.js,
    // provision-aging-service.js). Only present once dateShiftKind is set;
    // legacy rows moved before this was tracked won't have it.
    originalDate?: string | null;
};

export type FilterParams = {
    sortBy: 'description' | 'category' | 'amount' | 'date' | 'bank' | undefined | null,
    sortDirection: 'ASC' | 'DESC',
    amountKeyword?: [number, number],
    descriptionKeyword?: string,
    // Header search box. Separate from descriptionKeyword (the description column filter),
    // and combined with it and every other filter.
    searchKeyword?: string,
    categoryIds?: string[],
};

export interface TransactionsTableHandle {
    scrollToToday: () => void;
    // Saves the scroll container's current scrollTop, so a caller that's
    // about to trigger a background refresh (e.g. linking/closing a
    // transaction from its detail popup) can restore the exact same scroll
    // position afterward instead of the load-complete effect defaulting
    // back to "today", as if the refresh were a fresh navigation.
    captureScrollAnchor: () => void;
}

interface TransactionsTableProps {
    data: TransactionRow[];
    onEdit?: (row: TransactionRow) => void;
    onDelete?: (row: TransactionRow, deleteScope?: 'this-occurrence' | 'this-and-future' | 'all-occurrences') => void;
    onTerminate?: (row: TransactionRow) => void;
    onChangeDate?: (row: TransactionRow) => void;
    onLinkProvision?: (row: TransactionRow) => void;
    // The "Related" icon on a real transaction linked to a provision
    // template - distinct from onEdit (which represents editing the row
    // itself) since this row's own provisionId isn't set, only
    // provisionThisTemplateId.
    onEditProvisionTemplate?: (templateId: number) => void;
    onProvisionThis?: (row: TransactionRow) => void;
    onMarkTransactions?: (transactionGuids: string[], provisionId?: number) => void;
    onProvisionThese?: (transactionGuids: string[]) => void;
    onCategoryChange?: (transactionId: string, categoryId: number | null, applyDefaultTaxes: boolean) => void;
    // scope: a recurring provision occurrence's change - 'all' occurrences or 'this' one.
    onTaxChange?: (transactionId: string, appliedTaxIds: number[], scope?: 'this' | 'all') => void;
    taxOptions?: { id: number; name: string; rate: number }[];
    categoryDefaultTaxIds?: Record<string, number[]>;
    highlightRows?: (row: TransactionRow, idx: number) => {
        color?: string;
        border?: string;
        background?: string;
        fontWeight?: number | string;
    };
    columns: { id: string; label: string; width: number | string }[];
    setColumnFilter: React.Dispatch<React.SetStateAction<FilterParams | undefined>>;
    columnFilter?: FilterParams | undefined;
    onRowClick?: (id: string, date: string, bank: string, category: string, isSimulation?: boolean) => void;
    bankOptions: DropdownOption[];
    categoryOptions: DropdownOption[];
    hierarchicalCategories?: { id: string; label: string; children?: any[] }[];
    companyStatus?: {
        highestAmount?: number;
        lowestAmount?: number;
        latestTransaction?: Date | null;
        earliestTransaction?: Date | null;
        // New API fields (startOfRecords/endOfHorizon)
        startOfRecords?: { guid?: string | null; date?: Date | string | null } | null;
        endOfHorizon?: Date | string | null;
        // Optional cashflow info
        firstNegativeDate?: Date | string | null;
        firstNegativeTransactionGuid?: string | null;
        totalCount?: number;
    };
    stage?: 'initialize' | 'fetching' | 'complete';
    isHeaderFilterUsed?: boolean;
    startDate?: Date;
    endDate?: Date;
    viewBy?: string;
    // New setters/values so the date column filter can control header-level filters
    setViewBy?: (value: string) => void;
    unitValue?: string;
    setUnitValue?: (value: string) => void;
    isDateRange?: boolean;
    setIsDateRange?: (value: boolean) => void;
    setStartDate?: (value: Date) => void;
    setEndDate?: (value: Date) => void;
    // "older" direction - scrolling down, further into the past.
    hasNextPage?: boolean;
    onLoadMore?: () => void;
    isLoadingMore?: boolean;
    // "newer" direction - scrolling up, back toward/past the anchor date.
    hasNewer?: boolean;
    onLoadNewer?: () => void;
    isLoadingNewer?: boolean;
    // The current role's permissions (admins always pass). Real transactions
    // follow Transactions, provision rows follow Provisions; creating a
    // provision from transactions ("Provision This/These") follows Provisions
    // Create. Left out = admin-only. Disables (not hides) row actions/inline
    // edits.
    canUpdate?: boolean;
    canDelete?: boolean;
    canUpdateProvisions?: boolean;
    canDeleteProvisions?: boolean;
    canCreateProvisions?: boolean;
}

// Memoized checkbox component to prevent unnecessary re-renders
const TransactionCheckbox = memo(({ 
    rowId, 
    isChecked, 
    isHovered, 
    hasAnyChecked, 
    onToggle,
    disabled = false,
}: { 
    rowId: string; 
    isChecked: boolean; 
    isHovered: boolean; 
    hasAnyChecked: boolean; 
    onToggle: (rowId: string) => void;
    disabled?: boolean;
}) => {
    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        onToggle(rowId);
    }, [rowId, onToggle]);

    return (
        <Box display="flex" justifyContent="flex-end">
            <input
                type="checkbox"
                checked={isChecked}
                onChange={handleChange}
                disabled={disabled}
                title={disabled ? "You don't have permission to do this" : undefined}
                style={{
                    width: '20px',
                    height: '20px',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    accentColor: 'var(--color-primary)',
                    opacity: (isHovered || hasAnyChecked) ? 1 : 0.35,
                }}
            />
        </Box>
    );
});

TransactionCheckbox.displayName = 'TransactionCheckbox';

// rowData.appliedTaxIds is typed number[], but that's a compile-time-only
// guarantee - `?? []` alone only catches null/undefined, not other malformed
// non-array values (e.g. an un-parsed JSON string slipping through from the
// backend). That combination crashed the whole page: a truthy non-array made
// it past `?? []` and threw "X.map is not a function" during render.
const asTaxIdArray = (value: unknown): number[] => Array.isArray(value) ? value : [];

// A row's own sx.color (set in muiTableBodyRowProps below, even with
// !important) never overrides a cell's OWN explicit color - inheritance
// only kicks in when a descendant has no explicit value of its own. Every
// cell that colors its text by provision-vs-not must call this (mirroring
// the same categorization used for the row background) instead of
// hardcoding var(--color-provision-text), or it'll silently ignore the
// per-type customization on the Personalisation page.
const getProvisionTextColor = (rowData: TransactionRow): string => {
    if (rowData.type !== 'provision' || rowData.paidAtImport) return 'inherit';
    if (rowData.invoiceNumber) return 'var(--color-invoice-provision-text)';
    // Checked before isRecurring - a payroll template's own type is a
    // payment cycle (e.g. 'weekly'), never 'one-time', so isRecurring
    // resolves true for payroll rows too and would wrongly win below.
    if (rowData.staffPaymentId) return 'var(--color-payroll-provision-text)';
    if (rowData.isRecurring === true) return 'var(--color-recurring-provision-text)';
    return 'var(--color-onetime-provision-text)';
};

// Whether an unlinked provision's date has been shifted away from where the
// recurrence/invoice originally put it, and in which direction - drives the
// hourglass icon in the actions cell (row/text color no longer varies by
// this - see getProvisionTextColor above). pushedForward takes precedence
// when (in theory) both flags were somehow set.
const getProvisionDateShiftKind = (rowData: TransactionRow): 'pushedForward' | 'pulledBack' | null => {
    if (rowData.type !== 'provision' || !!rowData.linkedTransactionId) return null;
    if (rowData.pushedForwardForAging === true) return 'pushedForward';
    if (rowData.dateAdjustedBack === true) return 'pulledBack';
    return null;
};

// The former single "Action" column, split into compact icon-headed
// columns (see the header and cell renderers below).
const ICON_HEADER_IDS = new Set(['linked', 'moved', 'select', 'actions']);
const ICON_CELL_STYLE: React.CSSProperties = { display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' };

const TransactionsTableVirtual = forwardRef<TransactionsTableHandle, TransactionsTableProps>(({ data, onEdit, onDelete, onTerminate, onChangeDate, onLinkProvision, onEditProvisionTemplate, onProvisionThis, onMarkTransactions, onProvisionThese, onCategoryChange, onTaxChange, taxOptions = [], categoryDefaultTaxIds = {}, highlightRows, columns, setColumnFilter, columnFilter, onRowClick, bankOptions, categoryOptions, hierarchicalCategories, companyStatus, stage = 'fetching', isHeaderFilterUsed, startDate, endDate, viewBy, setViewBy, unitValue, setUnitValue, isDateRange, setIsDateRange, setStartDate, setEndDate, hasNextPage, onLoadMore, isLoadingMore, hasNewer, onLoadNewer, isLoadingNewer, canUpdate, canDelete, canUpdateProvisions, canDeleteProvisions, canCreateProvisions }, ref) => {
    const { formatDate, formatAmount } = useSettings();
    const { user } = useUser();
    // Server-verified per-entity admin flag - see table-virtual.tsx for why
    // User.role (login cookie) isn't used here.
    const { isAdmin } = usePermissions();
    // These used to also require being an admin, so a custom role that HAD
    // the permission still saw everything disabled.
    const editDisabled = !isAdmin && !canUpdate;
    const deleteDisabled = !isAdmin && !canDelete;
    const provisionEditDisabled = !isAdmin && !canUpdateProvisions;
    const provisionDeleteDisabled = !isAdmin && !canDeleteProvisions;
    const provisionCreateDisabled = !isAdmin && !canCreateProvisions;
    // Inline edits (category / tax) follow the row's own resource.
    const rowEditDisabled = (row: { type?: string }) => (row.type === 'provision' ? provisionEditDisabled : editDisabled);
    // One shared, stable list for every row's category dropdown - a fresh
    // array per row per render made each dropdown redo its tree setup.
    const categoryTreeOptions = React.useMemo(() => (hierarchicalCategories || []).filter(cat => cat.id !== 'select-all-categories'), [hierarchicalCategories]);
    const amountRef = useRef<HTMLDivElement>(null);
    const descriptionRef = useRef<HTMLDivElement>(null);
    const categoryRef = useRef<HTMLDivElement>(null);
    const dateRef = useRef<HTMLDivElement>(null);
    const bankRef = useRef<HTMLDivElement>(null);
    const actionsRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const currentDayRef = useRef<number | null>(null);
    const rowVirtualizerInstanceRef = useRef<any>(null);
    // Set by captureScrollAnchor (see TransactionsTableHandle) to the raw
    // scrollTop pixel value at capture time, consumed and cleared by the
    // load-complete anchor effect below - a straight scrollTop save/restore
    // around the refetch, rather than trying to re-locate a specific row
    // afterward.
    const pendingScrollTopRef = useRef<number | null>(null);
    const clearPendingScrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // The user's last scroll position while the table was idle - see the
    // in-place-update safety net below.
    const lastScrollTopRef = useRef(0);
    // Guards the near-top "load newer" trigger against firing during the
    // initial anchor-scroll (which briefly passes through scrollTop 0 on its
    // way to centering on today) - only arms once the user has actually
    // scrolled near the top themselves.
    const hasScrolledRef = useRef(false);
    // Set right before calling onLoadNewer(), consumed by the scroll-position
    // preservation effect once the prepend actually lands.
    const preservedNewerLoadRef = useRef<{ topIndex: number; lengthBefore: number } | null>(null);
    const [activeFilterPopup, setActiveFilterPopup] = useState<string>('');
    // Client-side only (filters whatever's currently loaded, not a server
    // query) - the Action column's icons (Linked/Moved/Related) aren't a
    // real column value to sort/query by, just per-row computed flags
    // already present on TransactionRow, so filtering in-memory here is
    // both sufficient and much simpler than adding a new backend param.
    const [actionFilter, setActionFilter] = useState<Set<ActionIconType>>(new Set());
    const [actionExcludeFilter, setActionExcludeFilter] = useState<Set<ActionIconType>>(new Set());
    // On by default each visit (not persisted) - an invoice provision's
    // description is stored as "<status/doc label> <invoiceNumber> -
    // <clientName>" (see provision-service.js), and that leading label
    // (e.g. "AUTHORISED 12435 - ...", "DRAFT INV-0315 - ...") is hidden by
    // default. Toggling this off shows it again, leaving everything else
    // (row visibility, balances, the stored description itself) untouched.
    const [hideInvoiceStatusLabel, setHideInvoiceStatusLabel] = useState<boolean>(true);
    const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
    const [checkedRows, setCheckedRows] = useState<Set<string>>(new Set());
    const [taxOverrides, setTaxOverrides] = useState<Record<string, number[]>>({});
    const [showProvisionSelector, setShowProvisionSelector] = useState(false);
    const [categoryChangeConfirm, setCategoryChangeConfirm] = useState<{ rowId: string; newCategoryId: number | null; currentTaxIds: number[] } | null>(null);
    const [taxChangeConfirm, setTaxChangeConfirm] = useState<{ rowId: string; next: number[] } | null>(null);
    const [taxScopeConfirm, setTaxScopeConfirm] = useState<{ rowId: string; next: number[] } | null>(null);
    const [filterConfig, setFilterConfig] = useState<FilterParams>({
        sortBy: 'date',
        sortDirection: 'DESC'
    });

    const sortableColumns = columns.filter(col => ['date', 'amount', 'description', 'category', 'bank'].includes(col.id));


    // Memoized checkbox toggle handler
    const handleCheckboxToggle = useCallback((rowId: string) => {
        setCheckedRows(prev => {
            const newChecked = new Set(prev);
            if (newChecked.has(rowId)) {
                newChecked.delete(rowId);
            } else {
                newChecked.add(rowId);
            }
            return newChecked;
        });
    }, []);

    // compute processed data including optional current-day placeholder and range indicators
    const processedData = React.useMemo(() => {
        const today = new Date();
        today.setHours(0,0,0,0);
        const hasCurrentDay = data.some(row => {
            const rowDate = new Date(row.date);
            rowDate.setHours(0, 0, 0, 0);
            return rowDate.getTime() === today.getTime();
        });

        let newData = hideInvoiceStatusLabel
            ? data.map(row => {
                if (!row.invoiceNumber) return row;
                const idx = row.description.indexOf(row.invoiceNumber);
                if (idx <= 0) return row;
                return { ...row, description: row.description.slice(idx) };
            })
            : [...data];

        if (actionFilter.size > 0 || actionExcludeFilter.size > 0) {
            const rowActionTypes = (row: TransactionRow): ActionIconType[] => {
                const types: ActionIconType[] = [];
                if (row.hasLinkedTransactions) types.push('linked');
                if (getProvisionDateShiftKind(row) !== null) types.push('moved');
                if (row.provisionThisTemplateId) types.push('related');
                return types;
            };
            newData = newData.filter(row => {
                const types = rowActionTypes(row);
                const matchesRequired = actionFilter.size === 0 || types.some(t => actionFilter.has(t));
                const matchesExcluded = types.some(t => actionExcludeFilter.has(t));
                return matchesRequired && !matchesExcluded;
            });
        }

        // If no data, add a no-data placeholder row
        if (newData.length === 0) {
            const noDataRow: TransactionRow = {
                id: 'no-data-placeholder',
                date: '',
                amount: '',
                balance: '',
                description: '',
                bank: '',
                category: '',
                type: 'real'
            };
            newData.push(noDataRow);
        } else {
            // Only add current-day-placeholder if we have data
            if (!hasCurrentDay && viewBy === 'transactions' && (filterConfig.sortBy === null || filterConfig.sortBy === 'date')) {
                const year = today.getFullYear();
                const month = String(today.getMonth() + 1).padStart(2, '0');
                const day = String(today.getDate()).padStart(2, '0');
                const todayStr = `${year}-${month}-${day}`;

                const blankRow: TransactionRow = {
                    id: 'current-day-placeholder',
                    date: todayStr,
                    amount: '',
                    balance: '',
                    description: '',
                    bank: '',
                    category: '',
                    type: 'real'
                };

                const firstDate = new Date(data[0].date);
                firstDate.setHours(0, 0, 0, 0);
                const secondDate = data.length > 1 ? new Date(data[1].date) : null;
                if (secondDate) secondDate.setHours(0, 0, 0, 0);

                const isDescending = secondDate ? firstDate.getTime() >= secondDate.getTime() : false;
                let inserted = false;
                if (isDescending) {
                    for (let i = 0; i < newData.length; i++) {
                        const rowDate = new Date(newData[i].date);
                        rowDate.setHours(0, 0, 0, 0);
                        if (rowDate.getTime() < today.getTime()) {
                            newData.splice(i, 0, blankRow);
                            inserted = true;
                            break;
                        }
                    }
                } else {
                    for (let i = 0; i < newData.length; i++) {
                        const rowDate = new Date(newData[i].date);
                        rowDate.setHours(0, 0, 0, 0);
                        if (rowDate.getTime() > today.getTime()) {
                            newData.splice(i, 0, blankRow);
                            inserted = true;
                            break;
                        }
                    }
                }
                if (!inserted) {
                    newData.push(blankRow);
                }
            }

            // Range indicator rows ("Start of Records"/"End of Horizon"/etc.)
            // only make sense when the list is actually in chronological
            // order - their labels and positioning both assume that. Sorting
            // by any other column breaks that assumption, so skip them
            // entirely rather than show a boundary claim that isn't true for
            // the list's current order.
            if (filterConfig.sortBy === null || filterConfig.sortBy === undefined || filterConfig.sortBy === 'date') {
                const topIndicatorRow: TransactionRow = {
                    id: 'range-indicator-top',
                    date: '',
                    amount: '',
                    balance: '',
                    description: '',
                    bank: '',
                    category: '',
                    type: 'real'
                };

                const bottomIndicatorRow: TransactionRow = {
                    id: 'range-indicator-bottom',
                    date: '',
                    amount: '',
                    balance: '',
                    description: '',
                    bank: '',
                    category: '',
                    type: 'real'
                };

                newData.unshift(topIndicatorRow);
                newData.push(bottomIndicatorRow);
            }
        }

        return newData;
    }, [data, viewBy, filterConfig, companyStatus, startDate, endDate, actionFilter, actionExcludeFilter, hideInvoiceStatusLabel]);

    const { topIndicatorType, topLabel, bottomIndicatorType, bottomLabel } = React.useMemo(() => {
        // Get earliest and latest from companyStatus (system boundaries)
        const earliestTxnDate = companyStatus?.earliestTransaction ? new Date(companyStatus.earliestTransaction) : null;
        const latestTxnDate = companyStatus?.latestTransaction ? new Date(companyStatus.latestTransaction) : null;

        // Determine which is "first" and "last" based on sort order
        const isDescending = filterConfig.sortDirection === 'DESC';
        const firstTxnDate = isDescending ? latestTxnDate : earliestTxnDate;
        const lastTxnDate = isDescending ? earliestTxnDate : latestTxnDate;

        // Top indicator logic (what appears at the top of the list)
        let topType: 'red' | 'orange' | 'none' = 'red';
        let topLbl = isDescending ? 'End of Horizon' : 'Start of Records';

        // With lazy loading, `data` only holds the currently-loaded window, not
        // the full result set - the top/bottom row is frequently just the edge
        // of what's been fetched so far, not a real boundary. `hasNewer`/
        // `hasNextPage` (see prop comments above) tell us whether more of the
        // (possibly filtered) result set is still waiting to be loaded in that
        // direction; only once there's nothing left to load does reaching this
        // edge actually mean "Start of Records" / "End of Horizon" / a filter
        // boundary - so skip the whole determination and render nothing until then.
        if (hasNewer) {
            topType = 'none';
            topLbl = '';
        } else if (isDescending && endDate && latestTxnDate) {
            const filterEnd = new Date(endDate);
            filterEnd.setHours(0, 0, 0, 0);
            const latest = new Date(latestTxnDate);
            latest.setHours(0, 0, 0, 0);

            if (filterEnd >= latest) {
                topType = 'red';
                topLbl = 'End of Horizon';
            } else if (filterEnd < latest) {
                topType = 'orange';
                topLbl = 'End of filter results';
            }
        } else if (!isDescending && startDate && earliestTxnDate) {
            const filterStart = new Date(startDate);
            filterStart.setHours(0, 0, 0, 0);
            const earliest = new Date(earliestTxnDate);
            earliest.setHours(0, 0, 0, 0);

            if (filterStart <= earliest) {
                topType = 'red';
                topLbl = 'Start of Records';
            } else if (filterStart > earliest) {
                topType = 'orange';
                topLbl = 'Beginning of filter results';
            }
        } else if (!startDate && !endDate) {
            topType = 'red';
            topLbl = isDescending ? 'End of Horizon' : 'Start of Records';
        }

        // Bottom indicator logic (what appears at the bottom of the list)
        let bottomType: 'red' | 'orange' | 'none' = 'red';
        let bottomLbl = isDescending ? 'Start of Records' : 'End of Horizon';

        if (hasNextPage) {
            bottomType = 'none';
            bottomLbl = '';
        } else if (isDescending && startDate && earliestTxnDate) {
            const filterStart = new Date(startDate);
            filterStart.setHours(0, 0, 0, 0);
            const earliest = new Date(earliestTxnDate);
            earliest.setHours(0, 0, 0, 0);

            if (filterStart <= earliest) {
                bottomType = 'red';
                bottomLbl = 'Start of Records';
            } else if (filterStart > earliest) {
                bottomType = 'orange';
                bottomLbl = 'Beginning of filter results';
            }
        } else if (!isDescending && endDate && latestTxnDate) {
            const filterEnd = new Date(endDate);
            filterEnd.setHours(0, 0, 0, 0);
            const latest = new Date(latestTxnDate);
            latest.setHours(0, 0, 0, 0);

            if (filterEnd >= latest) {
                bottomType = 'red';
                bottomLbl = 'End of Horizon';
            } else if (filterEnd < latest) {
                bottomType = 'orange';
                bottomLbl = 'End of filter results';
            }
        } else if (!startDate && !endDate) {
            bottomType = 'red';
            bottomLbl = isDescending ? 'Start of Records' : 'End of Horizon';
        }

        return { topIndicatorType: topType, topLabel: topLbl, bottomIndicatorType: bottomType, bottomLabel: bottomLbl };
    }, [startDate, endDate, companyStatus, filterConfig.sortDirection, hasNewer, hasNextPage]);

    // Only sync back to parent when user actually applies a filter, not on every change
    const handleApply = (params: Partial<FilterParams>) => {
        const newConfig = {
            ...filterConfig,
            ...params
        };
        setFilterConfig(newConfig);
        setColumnFilter(newConfig); // Only call this when user applies changes
        setActiveFilterPopup('');
    };

    const getColumnDisplayState = (colId: string) => {
        const isFilterActive = () => {
            switch (colId) {
                case 'amount':
                    return !!filterConfig.amountKeyword;
                case 'description':
                    return !!filterConfig.descriptionKeyword;
                case 'category':
                    return !!filterConfig.categoryIds?.length;
                case 'actions':
                    return actionFilter.size > 0 || actionExcludeFilter.size > 0;
                default:
                    return false;
            }
        };

        const isSortActive = filterConfig.sortBy === colId;
        const isPopupOpen = activeFilterPopup === colId;

        return {
            isFilterActive: isFilterActive(),
            isSortActive,
            isPopupOpen
        };
    };

    const filters = {
        amount: (
            <AmountFilter
                anchorEl={amountRef.current}
                open={activeFilterPopup === 'amount'}
                onClose={() => setActiveFilterPopup('')}
                min={companyStatus?.lowestAmount || 0}
                max={companyStatus?.highestAmount || 999999}
                onApply={handleApply}
                currentSort={{
                    sortBy: filterConfig.sortBy || undefined,
                    sortDirection: filterConfig.sortDirection
                }}
                currentAmountKeyword={filterConfig.amountKeyword}
            />
        ),
        description: (
            <DescriptionFilter
                anchorEl={descriptionRef.current}
                open={activeFilterPopup === 'description'}
                onClose={() => setActiveFilterPopup('')}
                onApply={handleApply}
                currentSort={{
                    sortBy: filterConfig.sortBy || undefined,
                    sortDirection: filterConfig.sortDirection
                }}
                currentDescriptionKeyword={filterConfig.descriptionKeyword}
                extraToggle={{
                    label: 'Hide status label in description',
                    checked: hideInvoiceStatusLabel,
                    onChange: setHideInvoiceStatusLabel,
                }}
            />
        ),
        category: (
            <CategoryFilter
                anchorEl={categoryRef.current}
                open={activeFilterPopup === 'category'}
                onClose={() => setActiveFilterPopup('')}
                onApply={handleApply}
                currentSort={{
                    sortBy: filterConfig.sortBy || undefined,
                    sortDirection: filterConfig.sortDirection
                }}
            />
        ),
        date: (
            <DateFilter
                anchorEl={dateRef.current}
                open={activeFilterPopup === 'date'}
                onClose={() => setActiveFilterPopup('')}
                onApply={handleApply}
                currentSort={{
                    sortBy: filterConfig.sortBy || undefined,
                    sortDirection: filterConfig.sortDirection
                }}
                viewBy={viewBy}
                setViewBy={setViewBy}
                unitValue={unitValue}
                setUnitValue={setUnitValue}
                isDateRange={isDateRange}
                setIsDateRange={setIsDateRange}
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                companyStatus={companyStatus}
            />
        ),
        bank: (
            <BankFilter
                anchorEl={bankRef.current}
                open={activeFilterPopup === 'bank'}
                onClose={() => setActiveFilterPopup('')}
                onApply={handleApply}
                currentSort={{
                    sortBy: filterConfig.sortBy || undefined,
                    sortDirection: filterConfig.sortDirection
                }}
            />
        ),
        actions: (
            <ActionsFilter
                anchorEl={actionsRef.current}
                open={activeFilterPopup === 'actions'}
                onClose={() => setActiveFilterPopup('')}
                selected={actionFilter}
                onChange={setActionFilter}
                excluded={actionExcludeFilter}
                onChangeExcluded={setActionExcludeFilter}
            />
        ),
    };

    // Sorting and filtering are separate controls now - clicking a column's
    // header label or its sort arrow always just sorts by that column
    // immediately; a distinct icon opens that column's filter popover
    // (which still offers the same "Sort By X" option inside, for parity
    // with whichever entry point someone's used to).
    const handleColumnSort = (colId: string) => {
        const newDirection: 'ASC' | 'DESC' = filterConfig.sortBy === colId && filterConfig.sortDirection === 'ASC' ? 'DESC' : 'ASC';
        const newConfig: FilterParams = {
            ...filterConfig,
            sortBy: colId as any,
            sortDirection: newDirection
        };
        setFilterConfig(newConfig);
        // Unlike handleApply (filter popover), this direct-click path was
        // only ever updating this component's own local state - the parent
        // page (which actually sorts the displayed rows) never learned the
        // new sort, so clicking a header rotated the arrow but never
        // reordered anything.
        setColumnFilter(newConfig);
    };

    const handleOpenColumnFilter = (colId: string) => {
        if (activeFilterPopup === colId) {
            setActiveFilterPopup('');
        } else {
            setActiveFilterPopup(colId);
        }
    };

    // build column definitions for MaterialReactTable
    const tableColumns = React.useMemo<MRT_ColumnDef<TransactionRow>[]>(() => {
        const cols: MRT_ColumnDef<TransactionRow>[] = [];

        columns.forEach(col => {
            const header = () => {
                const refMap: any = {
                    amount: amountRef,
                    date: dateRef,
                    description: descriptionRef,
                    category: categoryRef,
                    bank: bankRef,
                    actions: actionsRef,
                };
                if (col.id.startsWith('tax_')) {
                    const SKIP = new Set(['and','or','of','the','a','an','in','on','at','to','for','with']);
                    const words = col.label.trim().split(/\s+/);
                    const significant = words.filter(w => !SKIP.has(w.toLowerCase()));
                    const abbr = significant.length > 1
                        ? significant.map(w => w[0]).join('').toUpperCase().slice(0, 4)
                        : col.label.slice(0, 3).toUpperCase();
                    return (
                        <Box sx={{ width: 36, height: 22.5, overflow: 'visible', position: 'relative' }}>
                            <Tooltip title={col.label} componentsProps={{ tooltip: { sx: { backgroundColor: '#fffefb', color: 'var(--color-default-text)', fontSize: 13, border: '1px solid #e9e9e9', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' } } }} arrow>
                                <Typography sx={{
                                    fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)',
                                    position: 'absolute',
                                    bottom: 2,
                                    // left: '50%',
                                    transformOrigin: 'bottom left',
                                    // transform: 'rotate(-55deg) translateX(-50%)',
                                    whiteSpace: 'nowrap',
                                }}>
                                    {abbr}
                                </Typography>
                            </Tooltip>
                        </Box>
                    );
                }

                // Bulk actions for the ticked rows - lives in the Select column's
                // header once anything is ticked.
                const bulkMenu = (
                    <ActionMenu actions={[
                        {
                            label: (() => {
                                const selectableRows = data.filter(row => !row.provisionThisTemplateId && row.id !== 'current-day-placeholder' && row.type === 'real');
                                return checkedRows.size === selectableRows.length ? 'Unselect All' : 'Select All';
                            })(),
                            onClick: () => {
                                const selectableRows = data.filter(row => !row.provisionThisTemplateId && row.id !== 'current-day-placeholder' && row.type === 'real');
                                if (checkedRows.size === selectableRows.length) {
                                    setCheckedRows(new Set());
                                } else {
                                    setCheckedRows(new Set(selectableRows.map(row => row.id)));
                                }
                            },
                            icon: (() => {
                                const selectableRows = data.filter(row => !row.provisionThisTemplateId && row.id !== 'current-day-placeholder' && row.type === 'real');
                                return checkedRows.size === selectableRows.length ? 'close' : 'check';
                            })(),
                        },
                        {
                            label: 'Relate to Provision',
                            onClick: () => {
                                setShowProvisionSelector(true);
                            },
                            icon: 'link',
                            disabled: editDisabled,
                        },
                        ...(checkedRows.size >= 4
                            ? [
                                  {
                                      label: 'Provision These',
                                      onClick: () => {
                                          const selectedGuids = Array.from(checkedRows);
                                          if (onProvisionThese) {
                                              onProvisionThese(selectedGuids);
                                          }
                                          setCheckedRows(new Set());
                                      },
                                      icon: 'paste',
                                      // A provision template built from these rows' pattern doesn't
                                      // make sense if one of them is already accounted for by an
                                      // existing link.
                                      disabled: provisionCreateDisabled || data.some(row => checkedRows.has(row.id) && row.hasLinkedTransactions),
                                      tooltip: data.some(row => checkedRows.has(row.id) && row.hasLinkedTransactions)
                                          ? 'One or more selected transactions are already linked to a provision.'
                                          : undefined,
                                  },
                              ]
                            : []),
                    ]} />
                );

                // The four compact icon columns (Linked / Moved / Select /
                // Actions) are headed by an icon alone, with the column's name
                // as its tooltip.
                if (ICON_HEADER_IDS.has(col.id)) {
                    let content: React.ReactNode;
                    if (col.id === 'linked') content = <Link size={14} color="var(--color-primary)" />;
                    else if (col.id === 'moved') content = <CalendarClock size={14} color="var(--color-recurring-provision-text)" />;
                    else if (col.id === 'select') content = checkedRows.size > 0 ? bulkMenu : <SquareCheckBig size={14} color="var(--color-primary)" />;
                    else {
                        // Actions: the icon itself opens the Action filter, and turns
                        // teal while a filter is on.
                        const { isFilterActive } = getColumnDisplayState(col.id);
                        content = (
                            <IconButton
                                size="small"
                                onClick={() => handleOpenColumnFilter(col.id)}
                                tabIndex={-1}
                                aria-label="Filter by action"
                                sx={{ width: 22, height: 22, p: 0, borderRadius: '50px', backgroundColor: isFilterActive ? 'rgba(0, 182, 155, 0.15)' : 'transparent' }}
                            >
                                <Ellipsis size={16} color="var(--color-primary)" />
                            </IconButton>
                        );
                    }
                    // Same 24px height as the other headers' label rows (their
                    // sort/filter buttons), so the icons line up with the labels.
                    return (
                        <Box ref={refMap[col.id] || null} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: 24 }}>
                            {col.id === 'select' && checkedRows.size > 0 ? content : (
                                <Tooltip title={col.id === 'actions' ? 'Actions - click to filter' : col.label} arrow>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 24 }}>{content}</Box>
                                </Tooltip>
                            )}
                            <Box component="span" sx={{ display: 'contents' }} onKeyDown={(e) => e.stopPropagation()}>
                                {filters[col.id as keyof typeof filters]}
                            </Box>
                        </Box>
                    );
                }

                return (
                    <Box ref={refMap[col.id] || null} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography
                            onClick={sortableColumns.find(c => c.id === col.id) ? () => handleColumnSort(col.id) : undefined}
                            sx={sortableColumns.find(c => c.id === col.id) ? { cursor: 'pointer', userSelect: 'none', '&:hover': { color: 'var(--color-primary)' } } : undefined}
                        >
                            {col.label}
                        </Typography>
                        {sortableColumns.find(c => c.id === col.id) && (() => {
                            const { isFilterActive, isSortActive } = getColumnDisplayState(col.id);
                            return (
                                <>
                                    <IconButton
                                        size="small"
                                        style={{
                                            marginLeft: 4,
                                            color: isSortActive ? 'var(--color-primary)' : '#B0B0B0',
                                            backgroundColor: isSortActive ? 'rgba(0, 182, 155, 0.1)' : 'transparent',
                                            borderRadius: '50px',
                                            width: 24,
                                            height: 24,
                                        }}
                                        onClick={() => handleColumnSort(col.id)}
                                        tabIndex={-1}
                                        aria-label="Sort"
                                    >
                                        <Typography
                                            style={{
                                                opacity: isSortActive ? 0.9 : 0.3,
                                                color: isSortActive ? 'var(--color-primary)' : '#707070ff',
                                                fontSize: 12,
                                            }}
                                        >
                                            {isSortActive ? (filterConfig.sortDirection === 'ASC' ? '▲' : '▼') : '▼'}
                                        </Typography>
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        style={{
                                            marginLeft: 2,
                                            color: isFilterActive ? 'var(--color-primary)' : '#B0B0B0',
                                            backgroundColor: isFilterActive ? 'rgba(0, 182, 155, 0.1)' : 'transparent',
                                            borderRadius: '50px',
                                            width: 24,
                                            height: 24,
                                        }}
                                        onClick={() => handleOpenColumnFilter(col.id)}
                                        tabIndex={-1}
                                        aria-label="Filter"
                                    >
                                        <SlidersHorizontal size={12} color={isFilterActive ? 'var(--color-primary)' : '#B0B0B0'} />
                                    </IconButton>
                                </>
                            );
                        })()}
                        {/* Key presses inside a filter popup (portalled, but still
                            inside this header cell in React's tree) would bubble to
                            MaterialReactTable's cell keyboard navigation, which
                            preventDefault()s arrow/Home/End keys - so the caret
                            couldn't move in the popup's text fields. It skips events
                            whose propagation is already stopped. */}
                        <Box component="span" sx={{ display: 'contents' }} onKeyDown={(e) => e.stopPropagation()}>
                            {filters[col.id as keyof typeof filters]}
                        </Box>
                    </Box>
                );
            };

            const cellRenderer = ({ row, cell }: any) => {
                const rowData: TransactionRow = row.original;

                // Handle range indicator rows - render label in first column only
                if (rowData.id === 'range-indicator-top' || rowData.id === 'range-indicator-bottom') {
                    const isTopIndicator = rowData.id === 'range-indicator-top';
                    const indicatorLabel = isTopIndicator ? topLabel : bottomLabel;
                    
                    // Only render the indicator text in the first column
                    if (col.id === columns[0]?.id) {
                        return (
                            <Box sx={{ textAlign: 'center', fontSize: 12, fontWeight: 500, width: '100%' }}>
                                {indicatorLabel}
                            </Box>
                        );
                    }
                    // Other cells are hidden at the td level
                    return null;
                }

                // Handle current-day-placeholder - render label in first column only
                if (rowData.id === 'current-day-placeholder') {
                    // Only render the placeholder text in the first column
                    if (col.id === columns[0]?.id) {
                        return (
                            <Box sx={{ textAlign: 'center', fontSize: 12, fontWeight: 500, width: '100%' }}>
                                {formatDate(new Date())}
                            </Box>
                        );
                    }
                    // Other cells are hidden at the td level
                    return null;
                }

                // Handle no-data-placeholder - render TablePlaceholder component in first column only
                if (rowData.id === 'no-data-placeholder') {
                    // Only render the placeholder component in the first column
                    if (col.id === columns[0]?.id) {
                        return (
                            <TablePlaceholder
                                title="No Transactions Found"
                                message={(!companyStatus?.totalCount || companyStatus?.totalCount === 0) && !companyStatus?.earliestTransaction
                                    ? "Get started by importing transactions or creating categories."
                                    : `You have ${companyStatus?.totalCount || 0} transactions and provisions from ${companyStatus?.earliestTransaction ? formatDate(companyStatus?.earliestTransaction) : 'N/A'} to ${companyStatus?.latestTransaction ? formatDate(companyStatus?.latestTransaction) : 'N/A'}. Adjust your filters to view results.`}
                                latestTransaction={companyStatus?.latestTransaction}
                                earliestTransaction={companyStatus?.earliestTransaction}
                                totalCount={companyStatus?.totalCount}
                            />
                        );
                    }
                    // Other cells are hidden at the td level
                    return null;
                }

                if (col.id.startsWith('tax_')) {
                    const taxId = Number(col.id.replace('tax_', ''));
                    const effectiveIds = taxOverrides[rowData.id] ?? asTaxIdArray(rowData.appliedTaxIds);
                    const checked = effectiveIds.includes(taxId);
                    return (
                        <Box display="flex" justifyContent="center" onClick={e => e.stopPropagation()}>
                            <input
                                type="checkbox"
                                checked={checked}
                                disabled={rowData.isSimulation || rowEditDisabled(rowData)}
                                onChange={e => {
                                    const next = e.target.checked
                                        ? [...effectiveIds, taxId]
                                        : effectiveIds.filter(id => id !== taxId);

                                    // A recurring provision's occurrence - ask whether it's
                                    // for every occurrence or just this one.
                                    const isRecurringOccurrence = rowData.type === 'provision'
                                        && rowData.isRecurring === true
                                        && !rowData.paidAtImport
                                        && !rowData.invoiceNumber
                                        && !rowData.staffPaymentId;
                                    if (isRecurringOccurrence) {
                                        setTaxScopeConfirm({ rowId: rowData.id, next });
                                        return;
                                    }

                                    const categoryDefaults = categoryDefaultTaxIds[String(rowData.category)] ?? [];
                                    const matchesDefault = categoryDefaults.length === next.length && categoryDefaults.every(id => next.includes(id));

                                    if (!matchesDefault) {
                                        setTaxChangeConfirm({ rowId: rowData.id, next });
                                    } else {
                                        setTaxOverrides(prev => ({ ...prev, [rowData.id]: next }));
                                        onTaxChange?.(rowData.id, next);
                                    }
                                }}
                                style={{ width: 18, height: 18, accentColor: 'var(--color-primary)', cursor: 'pointer' }}
                            />
                        </Box>
                    );
                }

                switch (col.id) {
                    case 'date':
                        return (
                            <Typography sx={{ pr: 2, textAlign: viewBy !== 'week' ? 'right' : 'center', color: getProvisionTextColor(rowData) }}>
                                {(() => {
                                    // If row.date is a string and matches a year (e.g., "2025")
                                    if (typeof rowData.date === 'string' && /^\d{4}$/.test(rowData.date)) {
                                        return rowData.date;
                                    }
                                    // If row.date is a string formatted like "startDate to endDate"
                                    if (
                                        typeof rowData.date === 'string' &&
                                        rowData.date.includes(' to ')
                                    ) {
                                        const [start, end] = rowData.date.split(' to ');
                                        const startDateObj = new Date(start);
                                        const endDateObj = new Date(end);
                                        const startFormatted = formatDate(startDateObj);
                                        const endFormatted = formatDate(endDateObj);
                                        
                                        // Use arrow format for weekly view
                                        if (viewBy === 'week') {
                                            return `${startFormatted}⇾${endFormatted}`;
                                        }
                                        // For monthly view, show only month and year
                                        if (viewBy === 'month') {
                                            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                            const month = monthNames[endDateObj.getMonth()];
                                            const year = endDateObj.getFullYear();
                                            return `${month} ${year}`;
                                        }
                                        // For quarterly view, show Q# and year with tooltip showing coverage
                                        if (viewBy === 'quarter') {
                                            const month = endDateObj.getMonth();
                                            const quarter = Math.floor(month / 3) + 1;
                                            const year = endDateObj.getFullYear();
                                            return (
                                                <Tooltip title={`${startFormatted} to ${endFormatted}`} arrow componentsProps={{ tooltip: { sx: { backgroundColor: '#fffefb', color: 'var(--color-default-text)', fontSize: 13, border: '1px solid #e9e9e9', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' } } }}>
                                                    <span>{`Q${quarter} ${year}`}</span>
                                                </Tooltip>
                                            );
                                        }
                                        // For day view, show just the date
                                        if (viewBy === 'day') {
                                            return formatDate(endDateObj);
                                        }
                                        // For year view, show just the year
                                        if (viewBy === 'year') {
                                            return String(endDateObj.getFullYear());
                                        }
                                        return `${startFormatted} to ${endFormatted}`;
                                    }
                                    // Otherwise, treat as a normal date string
                                    return formatDate(new Date(rowData.date));
                                })()}
                            </Typography>
                        );
                    case 'amount':
                        return (
                            // Outer flex + justifyContent (not just the inner
                            // Typography's width:100%/textAlign) - on a narrow
                            // enough screen this column's grid cell can shrink
                            // below what width:100% alone reliably fills, at
                            // which point the Typography shrink-wraps to its
                            // own text width again and textAlign has nothing
                            // left to push against, making the value look
                            // centered instead of right-aligned. justifyContent
                            // on the wrapper keeps it pinned to the end either way.
                            // nowrap + pr: 1 (not 2) - the value never wraps, and
                            // less padding leaves room for the full "$ 25,255.00"
                            // at the column's base width on a narrow screen.
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                                <Typography sx={{ pr: 1, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                    <span
                                        style={{
                                            color: Number(rowData.amount) <= 0
                                                ? 'var(--color-negative-amount)'
                                                : 'var(--color-default-text)',
                                            fontWeight: 400 }}>
                                        {formatAmount ? formatAmount(Number(rowData.amount)) : rowData.amount}
                                    </span>
                                </Typography>
                            </Box>
                        );
                    case 'balance':
                        // null/undefined means the perf-diagnostic "Cash Position" toggle
                        // (see /secret-options) turned off the running-balance
                        // calculation server-side - show a dash instead of a
                        // misleading $0.00. excludedFromBalance is the same
                        // idea for a single row: its balance number is real
                        // but carried over from the prior row (this one's
                        // amount wasn't counted), so a dash is less misleading.
                        if (rowData.balance === null || rowData.balance === undefined || rowData.balance === '' || rowData.excludedFromBalance) {
                            return (
                                <Typography sx={{ pr: 2, textAlign: 'right', width: '100%', color: 'var(--color-default-text)' }}>
                                    -
                                </Typography>
                            );
                        }
                        return (
                            <Typography sx={{ pr: 1, textAlign: 'right', width: '100%', whiteSpace: 'nowrap' }}>
                                <span
                                    style={{
                                        color: Number(rowData.balance) <= 0
                                            ? 'var(--color-negative-amount)'
                                            : 'var(--color-default-text)',
                                        fontWeight: 400 }}>
                                    {formatAmount ? formatAmount(Number(rowData.balance)) : rowData.balance}
                                </span>
                            </Typography>
                        );
                    case 'description': {
                        const descriptionText = reformatGstDescriptionDates(rowData.description, formatDate);
                        // For a Xero-reconciled row, the cell shows Xero's
                        // preferred description (applyXeroDescriptionOverlay,
                        // transaction-controller.js) - the tooltip surfaces
                        // the original underlying value instead of just
                        // repeating what's already visible, so the RefreshCw
                        // icon's "open this transaction to see the original
                        // values" isn't the only way to check it.
                        const hasOriginal = rowData.reconciledWithXero && !!rowData.originalDescription;
                        // QA: hovering was only ever showing one or the other
                        // (the original replacing the full description
                        // entirely) instead of both - show the full current
                        // (possibly Xero-overridden) text first, then the
                        // original underneath it.
                        const tooltipText = hasOriginal
                            ? (
                                <>
                                    {descriptionText}
                                    <br />
                                    Original description: {reformatGstDescriptionDates(rowData.originalDescription!, formatDate)}
                                </>
                            )
                            : descriptionText;
                        return (
                            <Typography sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textAlign: 'left', color: getProvisionTextColor(rowData) }}>
                                {(rowData.reconciledWithXero || rowData.xeroTransactionId) && (
                                    <Tooltip title="Category/description were updated during Xero reconciliation - open this transaction to see the original values" arrow placement="top">
                                        <RefreshCw size={12} style={{ flexShrink: 0 }} />
                                    </Tooltip>
                                )}
                                <Tooltip
                                    title={tooltipText}
                                    componentsProps={{
                                        tooltip: {
                                            sx: {
                                                backgroundColor: '#fffefb',
                                                color: '#333333',
                                                fontSize: '14px',
                                                border: '1px solid #e9e9e9',
                                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                                maxWidth: 400,
                                            }
                                        }
                                    }}
                                    arrow
                                    placement="top"
                                >
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {descriptionText}
                                    </span>
                                </Tooltip>
                            </Typography>
                        );
                    }
                    case 'bank':
                        return (
                            <Typography sx={{ color: getProvisionTextColor(rowData) }}>
                                {(() => {
                                    const bankLabel = String(bankOptions.find(option => String(option.value) === String(rowData.bank))?.label || rowData.bank || '');
                                    const bankMatch = bankLabel.match(/^([^(]+)\s*\(([^)]+)\)$/);
                                    const bankName = bankMatch ? bankMatch[1].trim() : '';
                                    const accountName = bankMatch ? bankMatch[2].trim() : bankLabel;
                                    // Column only has room for initials (see baseColumns in
                                    // transactions-virtual.tsx) - the full name is still one
                                    // hover away via the tooltip below. Splitting only on
                                    // whitespace misses a name like "UnionBank" (one
                                    // whitespace-delimited word but two real words jammed
                                    // together) - split PascalCase/camelCase boundaries too,
                                    // so "UnionBank" reads as ["Union","Bank"] rather than
                                    // falling back to a meaningless 4-letter prefix. A run of
                                    // capitals like "ANZ" has no lowercase-to-uppercase
                                    // transition, so it's left intact.
                                    const SKIP_WORDS = new Set(['the', 'of', 'and', 'a', 'an', 'for']);
                                    const splitCamelCase = (s: string) => s
                                        .split(/(?<=[a-z0-9])(?=[A-Z])|(?<=[A-Z])(?=[A-Z][a-z])/)
                                        .filter(Boolean);
                                    const words = accountName.trim().split(/\s+/).filter(Boolean).flatMap(splitCamelCase);
                                    const significantWords = words.filter(w => !SKIP_WORDS.has(w.toLowerCase()));
                                    const relevantWords = significantWords.length > 0 ? significantWords : words;
                                    const initials = relevantWords.length > 1
                                        ? relevantWords.map(word => word[0]).join('').toUpperCase().slice(0, 5)
                                        : accountName.slice(0, 4).toUpperCase();
                                    const tooltipTitle = bankName ? `${bankName} - ${accountName}` : accountName;

                                    return (
                                        <Tooltip
                                            title={tooltipTitle}
                                            componentsProps={{
                                                tooltip: {
                                                    sx: {
                                                        backgroundColor: '#fffefb',
                                                        color: '#333333',
                                                        fontSize: '14px',
                                                        border: '1px solid #e9e9e9',
                                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                                    }
                                                }
                                            }}
                                            arrow>
                                            <span>{initials || accountName}</span>
                                        </Tooltip>
                                    );
                                })()}
                            </Typography>
                        );
                    case 'category':
                        return (
                            <div onClick={(e) => e.stopPropagation()}>
                                <TreeDropdown
                                    enableHighlightMode
                                    options={categoryTreeOptions}
                                    value={String(rowData.category)}
                                    onChange={(selectedValue) => {
                                        const newCategoryId = selectedValue ? Number(selectedValue) : null;
                                        const currentTaxIds = taxOverrides[rowData.id] ?? asTaxIdArray(rowData.appliedTaxIds);
                                        if (currentTaxIds.length > 0) {
                                            setCategoryChangeConfirm({ rowId: rowData.id, newCategoryId, currentTaxIds });
                                        } else {
                                            onCategoryChange?.(rowData.id, newCategoryId, true);
                                        }
                                    }}
                                    placeholder="Select..."
                                    height={24}
                                    width={180}
                                    borderColor="var(--color-primary)"
                                    disabled={rowData.isSimulation || rowEditDisabled(rowData)}
                                />
                            </div>
                        );
                    // The former Action column, one compact column per icon.
                    case 'linked': {
                        if (rowData.isSimulation) return null;
                        return (
                            <div style={ICON_CELL_STYLE} onClick={(e) => e.stopPropagation()}>
                                {rowData.hasLinkedTransactions &&
                                    <Tooltip
                                        title={rowData.type === 'provision' ? 'This provision is linked to a transaction' : 'This transaction is linked to a provision'}
                                        componentsProps={{
                                            tooltip: {
                                                sx: {
                                                    backgroundColor: '#fffefb',
                                                    color: '#333333',
                                                    fontSize: '14px',
                                                    border: '1px solid #e9e9e9',
                                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                                }
                                            }
                                        }}
                                        arrow
                                    >
                                        <Box display="flex" justifyContent="flex-end">
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                                <Link size={18} color={(rowData.type === 'real' || rowData.type === null) ? 'var(--color-primary)' : getProvisionTextColor(rowData)}/>
                                            </Box>
                                        </Box>
                                    </Tooltip>
                                }
                            </div>
                        );
                    }
                    case 'moved': {
                        if (rowData.isSimulation) return null;
                        // Desktop shows Linked/Moved as their own icons (room for
                        // both columns) - only the compact mobile card cycles them
                        // on the action button itself via ActionMenu's statusIcons
                        // prop (see transactions-table-mobile.tsx).
                        const dateShiftKind = getProvisionDateShiftKind(rowData);
                        return (
                            <div style={ICON_CELL_STYLE} onClick={(e) => e.stopPropagation()}>
                                {dateShiftKind &&
                                    <Tooltip
                                        title={rowData.originalDate
                                            ? `Originally scheduled for ${formatDate(new Date(rowData.originalDate))}`
                                            : dateShiftKind === 'pushedForward'
                                                ? 'This provision was pushed forward by the aging job'
                                                : "This provision's date was adjusted"}
                                        componentsProps={{
                                            tooltip: {
                                                sx: {
                                                    backgroundColor: '#fffefb',
                                                    color: '#333333',
                                                    fontSize: '14px',
                                                    border: '1px solid #e9e9e9',
                                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                                }
                                            }
                                        }}
                                        arrow
                                    >
                                        <Box display="flex" justifyContent="flex-end">
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                                <CalendarClock size={16} color={getProvisionTextColor(rowData)}/>
                                            </Box>
                                        </Box>
                                    </Tooltip>
                                }
                            </div>
                        );
                    }
                    case 'select': {
                        if (rowData.isSimulation) return null;
                        return (
                            <div style={ICON_CELL_STYLE} onClick={(e) => e.stopPropagation()}>
                                {rowData.provisionThisTemplateId ? (
                                    <Box display="flex" justifyContent="flex-end">
                                        <Tooltip 
                                            title="This transaction is related to a provision"
                                            componentsProps={{
                                                tooltip: {
                                                    sx: {
                                                        backgroundColor: '#fffefb',
                                                        color: '#333333',
                                                        fontSize: '14px',
                                                        border: '1px solid #e9e9e9',
                                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                                    }
                                                }
                                            }}
                                            arrow
                                        >
                                            <Box
                                                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
                                                onClick={()=> rowData.provisionThisTemplateId && onEditProvisionTemplate?.(rowData.provisionThisTemplateId)}
                                            >
                                                <SquareCheckBig size={20} color={'var(--color-primary)'}/>
                                            </Box>
                                        </Tooltip>
                                    </Box>
                                ) : (
                                    (rowData.type === 'real') && (
                                        <TransactionCheckbox
                                            // Only feeds the bulk actions (Relate to Provision /
                                            // Provision These) - disabled when neither is allowed.
                                            disabled={editDisabled && provisionCreateDisabled}
                                            rowId={rowData.id}
                                            isChecked={checkedRows.has(rowData.id)}
                                            isHovered={hoveredRowId === rowData.id}
                                            hasAnyChecked={checkedRows.size > 0}
                                            onToggle={handleCheckboxToggle}
                                        />
                                    )
                                )}
                            </div>
                        );
                    }
                    case 'actions': {
                        if (rowData.isSimulation) {
                            return (
                                <Tooltip title="Simulated data" arrow>
                                    <div style={ICON_CELL_STYLE}><Atom size={18} color={'var(--color-primary)'} /></div>
                                </Tooltip>
                            );
                        }
                        return (
                            <div style={ICON_CELL_STYLE} onClick={(e) => e.stopPropagation()}>
                                { (rowData.type === 'real') &&
                                    <ActionMenu
                                        actions={[
                                        {
                                            label: 'Link a Provision',
                                            onClick: () => onLinkProvision && onLinkProvision(rowData),
                                            icon: 'link',
                                            disabled: editDisabled || rowData.isLinkable === false || !!rowData.provisionThisTemplateId,
                                            tooltip: rowData.provisionThisTemplateId
                                                ? 'This transaction is already tracked by a provision via Provision This.'
                                                : rowData.isLinkable === false ? 'This transaction is closed and cannot accept more links.' : undefined,
                                        },
                                        ...(!rowData.provisionThisTemplateId ? [{
                                            label: 'Provision This',
                                            onClick: () => onProvisionThis && onProvisionThis(rowData),
                                            icon: 'paste' as const,
                                            disabled: provisionCreateDisabled || !!rowData.hasLinkedTransactions,
                                            tooltip: rowData.hasLinkedTransactions ? 'This transaction is linked to a provision.' : undefined,
                                        }] : []),
                                        // Only for transactions added via "+ Transaction" - imported
                                        // ones never get these. Blocked while linked (either
                                        // direction): editing/deleting would silently change a
                                        // provision's remaining amount, so unlink first (also
                                        // enforced server-side).
                                        ...(rowData.isManual ? [
                                            {
                                                label: 'Edit',
                                                onClick: () => onEdit && onEdit(rowData),
                                                icon: 'edit' as const,
                                                disabled: editDisabled || !!rowData.hasLinkedTransactions || !!rowData.provisionThisTemplateId,
                                                tooltip: (rowData.hasLinkedTransactions || rowData.provisionThisTemplateId) ? 'Unlink this transaction from its provision before editing it.' : undefined,
                                            },
                                            {
                                                label: 'Delete',
                                                onClick: () => onDelete && onDelete(rowData),
                                                icon: 'delete' as const,
                                                disabled: deleteDisabled || !!rowData.hasLinkedTransactions || !!rowData.provisionThisTemplateId,
                                                tooltip: (rowData.hasLinkedTransactions || rowData.provisionThisTemplateId) ? 'Unlink this transaction from its provision before deleting it.' : undefined,
                                            },
                                        ] : []),
                                    ]} />
                                }
                                { (rowData.type === 'provision') &&
                                    <ActionMenu
                                        actions={[
                                        { label: 'Edit', onClick: () => onEdit && onEdit(rowData), icon: 'edit', disabled: provisionEditDisabled },
                                        // Applies uniformly regardless of provision sub-type (recurring,
                                        // one-time, invoice-generated, etc.) - same OPEN/CLOSED gate as
                                        // the real-transaction side's "Link a Provision" above.
                                        {
                                            label: 'Link a Transaction',
                                            onClick: () => onLinkProvision && onLinkProvision(rowData),
                                            icon: 'link',
                                            disabled: provisionEditDisabled || rowData.isLinkable === false,
                                            tooltip: rowData.isLinkable === false ? 'This provision is closed and cannot accept more links.' : undefined,
                                        },
                                        // Linked provisions represent money already settled against a
                                        // real transaction - changing their date doesn't make sense
                                        // (matches the server-side guard in updateProvisionDate).
                                        ...(!rowData.linkedTransactionId ? [{ label: 'Move', onClick: () => onChangeDate && onChangeDate(rowData), icon: 'calendar' as const, disabled: provisionEditDisabled }] : []),
                                        rowData.isRecurring ? {
                                            label: 'Delete',
                                            icon: 'delete' as const,
                                            disabled: provisionDeleteDisabled && provisionEditDisabled,
                                            submenu: [
                                                { label: 'Delete this occurrence only', onClick: () => onDelete && onDelete(rowData, 'this-occurrence'), icon: 'delete' as const, disabled: provisionDeleteDisabled },
                                                { label: 'Delete this and future occurrences', onClick: () => onDelete && onDelete(rowData, 'this-and-future'), icon: 'delete' as const, disabled: provisionDeleteDisabled },
                                                { label: 'Delete all occurrences', onClick: () => onDelete && onDelete(rowData, 'all-occurrences'), icon: 'delete' as const, disabled: provisionDeleteDisabled },
                                                { label: 'Terminate', onClick: () => onTerminate && onTerminate(rowData), icon: 'warning' as const, disabled: provisionEditDisabled },
                                            ],
                                        } : { label: 'Delete', onClick: () => onDelete && onDelete(rowData), icon: 'delete' as const, disabled: provisionDeleteDisabled }
                                    ]} />
                                }
                            </div>
                        );
                    }
                    default:
                        return null;
                }
            };

            cols.push({
                accessorKey: col.id,
                header: col.label || '',
                Header: header,
                Cell: cellRenderer,
                enableSorting: false,
                size: typeof col.width === 'number' ? (col.width as number) : undefined,
                // MRT's default minimum (40px) would widen the compact icon columns.
                ...(ICON_HEADER_IDS.has(col.id) ? { minSize: col.width as number, muiTableHeadCellProps: { sx: {
                    px: 0,
                    background: 'var(--color-container-background)',
                    // MRT's header wrappers keep their content left-aligned (and
                    // pad for sort/resize controls these columns don't use) - stretch
                    // and center them so the icon sits over the cells' icons.
                    '& .Mui-TableHeadCell-Content, & .Mui-TableHeadCell-Content-Labels, & .Mui-TableHeadCell-Content-Wrapper': {
                        width: '100%', minWidth: 0, justifyContent: 'center', pl: 0, pr: 0, overflow: 'visible',
                    },
                } } } : {}),
                muiTableBodyCellProps: ({ row }: { row: MRT_Row<TransactionRow> }) => {
                    const rowData = row.original;
                    // For range indicator rows, current-day-placeholder, and no-data-placeholder, hide all cells except the first one
                    if (rowData.id === 'range-indicator-top' || rowData.id === 'range-indicator-bottom' || rowData.id === 'current-day-placeholder' || rowData.id === 'no-data-placeholder') {
                        if (col.id === columns[0]?.id) {
                            return {
                                sx: {
                                    width: '100%',
                                    padding: 0,
                                    border: 'none',
                                    flex: 1
                                }
                            };
                        }
                        return {
                            sx: {
                                width: 0,
                                padding: 0,
                                border: 'none',
                                display: 'none !important',
                                flex: 0
                            }
                        };
                    }
                    // Icon columns: no side padding, so a 32px column still fits its icon.
                    return ICON_HEADER_IDS.has(col.id) ? { sx: { px: 0 } } : {};
                }
            });
        });

        return cols;
    }, [
        data,
        columns,
        filterConfig,
        hoveredRowId,
        checkedRows,
        viewBy,
        amountRef,
        dateRef,
        descriptionRef,
        categoryRef,
        bankRef,
        filters,
        handleColumnSort,
        handleOpenColumnFilter,
        formatDate,
        formatAmount,
        onCategoryChange,
        hierarchicalCategories,
        onEdit,
        onDelete,
        onTerminate,
        onLinkProvision,
        onEditProvisionTemplate,
        onProvisionThis,
        onMarkTransactions,
        bankOptions,
        categoryOptions,
        taxOverrides,
        categoryDefaultTaxIds,
    ]);

    // Reset checkboxes when company changes
    useEffect(() => {
        setCheckedRows(new Set());
    }, [user?.entityId]);

    // Exposes an imperative "jump to today" action for the parent page's
    // topbar "Today" button - a no-op if today isn't in the currently
    // loaded/filtered data. Recomputes the index fresh from processedData
    // rather than trusting currentDayRef, which only gets refreshed by the
    // auto-scroll effect below (on load/filter change) - a "load more" or
    // "load newer" infinite-scroll fetch shifts every row's index without
    // re-running that effect, so the ref alone would go stale after one.
    useImperativeHandle(ref, () => ({
        scrollToToday: () => {
            const virtualizer = rowVirtualizerInstanceRef.current;
            if (!virtualizer) return;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const index = processedData.findIndex(row => {
                if (row.id === 'current-day-placeholder') return true;
                const rowDate = new Date(row.date);
                rowDate.setHours(0, 0, 0, 0);
                return rowDate.getTime() === today.getTime();
            });
            if (index !== -1) {
                virtualizer.scrollToIndex(index, { align: 'center', smoothScroll: false });
            }
        },
        captureScrollAnchor: () => {
            pendingScrollTopRef.current = containerRef.current?.scrollTop ?? null;
        },
    }), [processedData]);

    // Infinite scroll: load more when user scrolls near either edge of the
    // wrapper - bottom loads older rows (appended), top loads newer rows
    // (prepended - see the scroll-position-preservation effect below, since
    // prepending shifts every existing row's index).
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;
            const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
            if (scrollPercentage > 0.8 && hasNextPage && !isLoadingMore && onLoadMore) {
                onLoadMore();
            }
            // Guard against firing on the initial mount/anchor-scroll, before
            // the user has actually scrolled anywhere near the top - the
            // scroll-to-today effect briefly leaves scrollTop at 0 on the way
            // to its centered position, which would otherwise trigger this
            // immediately on every load.
            if (scrollTop < 200 && hasScrolledRef.current && hasNewer && !isLoadingNewer && onLoadNewer) {
                const virtualizer = rowVirtualizerInstanceRef.current;
                const topIndex = virtualizer?.getVirtualItems?.()[0]?.index;
                if (topIndex != null) {
                    preservedNewerLoadRef.current = { topIndex, lengthBefore: processedData.length };
                }
                onLoadNewer();
            }
            if (scrollTop > 200) hasScrolledRef.current = true;
            if (stage === 'complete') lastScrollTopRef.current = scrollTop;
        };

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [hasNextPage, isLoadingMore, onLoadMore, hasNewer, isLoadingNewer, onLoadNewer, processedData.length, stage]);

    // Safety net for in-place updates (e.g. an inline category/tax edit
    // patching one row): if the rows change while nothing is loading and the
    // scroll has snapped back to the top, put it back where the user was. A
    // fresh load (filter/date range/view change) resets the remembered
    // position first, so it still lands on "today" as before.
    useEffect(() => {
        if (stage !== 'complete') lastScrollTopRef.current = 0;
    }, [stage]);
    React.useLayoutEffect(() => {
        if (stage !== 'complete' || pendingScrollTopRef.current != null) return;
        const container = containerRef.current;
        const remembered = lastScrollTopRef.current;
        if (container && remembered > 0 && container.scrollTop === 0) {
            container.scrollTop = remembered;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [processedData]);

    // Restore scroll position after a "newer" load prepends rows - without
    // this, every existing row's index shifts up by however many rows were
    // added at the start, and the virtualizer would keep showing the same
    // index range (now different content), making the list appear to jump.
    useEffect(() => {
        const pending = preservedNewerLoadRef.current;
        if (!pending || isLoadingNewer) return;
        const delta = processedData.length - pending.lengthBefore;
        preservedNewerLoadRef.current = null;
        if (delta <= 0) return;
        const virtualizer = rowVirtualizerInstanceRef.current;
        if (virtualizer) {
            virtualizer.scrollToIndex(pending.topIndex + delta, { align: 'start', smoothScroll: false });
        }
    }, [isLoadingNewer, processedData.length]);

    // Initialize filterConfig from columnFilter prop when it changes
    useEffect(() => {
        if (columnFilter) {
            // If sortBy is null (filters cleared), use date as default
            setFilterConfig({
                ...columnFilter,
                sortBy: columnFilter.sortBy || 'date'
            });
        } else {
            // Initialize with defaults if no columnFilter
            setFilterConfig({
                sortBy: 'date',
                sortDirection: 'DESC'
            });
        }
    }, [columnFilter]);

    // Auto-scroll to current day after loading or when filters change
    useEffect(() => {
        // A fresh anchored load is starting (or just landed) for this
        // filter/view - the near-top "load newer" trigger shouldn't fire
        // again until the user has actually scrolled in this new context.
        hasScrolledRef.current = false;
        if (stage === 'complete' && containerRef.current && processedData.length > 0 && pendingScrollTopRef.current != null) {
            // A caller asked to restore the same scroll position across this
            // reload (see captureScrollAnchor) - honor that instead of the
            // default today-anchor below, then consume it so it doesn't also
            // apply to the next unrelated load.
            // Kept (not cleared right away) for a moment: some actions
            // refetch twice back-to-back (e.g. delete), and the second load
            // completing must land on the same spot rather than "today".
            const scrollTop = pendingScrollTopRef.current;
            setTimeout(() => {
                if (containerRef.current) containerRef.current.scrollTop = scrollTop;
            }, 100);
            if (clearPendingScrollTimerRef.current) clearTimeout(clearPendingScrollTimerRef.current);
            clearPendingScrollTimerRef.current = setTimeout(() => {
                pendingScrollTopRef.current = null;
                clearPendingScrollTimerRef.current = null;
            }, 2000);
            return;
        }
        if (stage === 'complete' && rowVirtualizerInstanceRef.current && processedData.length > 0) {
            // Find the index of the current day row
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const currentDayIndex = processedData.findIndex(row => {
                if (row.id === 'current-day-placeholder') return true;
                const rowDate = new Date(row.date);
                rowDate.setHours(0, 0, 0, 0);
                return rowDate.getTime() === today.getTime();
            });

            if (currentDayIndex !== -1) {
                currentDayRef.current = currentDayIndex;
                // Use a timeout to ensure DOM has updated
                setTimeout(() => {
                    const virtualizer = rowVirtualizerInstanceRef.current;
                    if (virtualizer) {
                        virtualizer.scrollToIndex(currentDayIndex, { align: 'center', smoothScroll: false });
                    }
                }, 100);
            } else {
                // Today isn't in this view at all (e.g. filtered out of the
                // current date range) - nothing for the "Today" button to scroll to.
                currentDayRef.current = null;
            }
        } else {
            currentDayRef.current = null;
        }
        // processedData is deliberately not a dependency here - it gets a new
        // array reference on every local row patch (e.g. an inline category
        // edit just does setData(prev => prev.map(...))), which would
        // re-trigger this and yank the user's scroll position back to today
        // mid-edit. It should only re-run when the view itself actually
        // changes (load completing, or a filter/date range/view switch) -
        // the effect still reads the current processedData value when it does.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stage, columnFilter, isHeaderFilterUsed, startDate, endDate, viewBy]);

    return (
        <Paper sx={{ width: '100%', borderRadius: 0, background: 'var(--color-page-background)', p: 4, boxSizing: 'border-box' }}>
            {stage !== 'complete' ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, flexDirection: 'column', gap: 2 }}>
                    <CircularProgress />
                    <Typography sx={{ ml: 2 }} fontSize={16}>
                        {stage === 'initialize' ? 'Initializing filters...' : 'Loading Transactions...'}
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ position: 'relative', width: '100%' }}>
                    <MaterialReactTable
                        columns={tableColumns}
                        data={processedData}
                        getRowId={(row) => String(row.id)}
                        enableRowVirtualization
                        enableColumnVirtualization
                        // Without this, MRT's default "semantic" layout treats each
                        // column's size as a proportion of the total rather than a
                        // fixed pixel width, so on a narrow screen every column
                        // (including Amount) shrinks together to keep summing to
                        // 100% instead of the table overflowing horizontally - the
                        // exact squish this is fixing. "grid" keeps each column at
                        // its defined size and lets the container scroll instead.
                        layoutMode="grid"
                        rowVirtualizerInstanceRef={rowVirtualizerInstanceRef}
                        rowVirtualizerOptions={{ overscan: 20 }}
                        columnVirtualizerOptions={{ overscan: 2 }}
                        enablePagination={false}
                        enableTopToolbar={false}
                        enableBottomToolbar={false}
                        enableColumnActions={false}
                        initialState={{ density: 'compact' }}
                        muiTableContainerProps={{ sx: { height: 'calc(100vh - 160px)', overflowY: 'auto', overflowX: 'auto', position: 'relative' }, ref: containerRef }}
                        muiTablePaperProps={{ sx: { boxShadow: 'none' } }}
                        muiTableHeadCellProps={{ sx: { background: 'var(--color-container-background)' } }}
                        muiTableBodyRowProps={({ row }: { row: MRT_Row<TransactionRow> }) => {
                            const rowData = row.original;
                            
                            // Handle range indicator rows
                            if (rowData.id === 'range-indicator-top' || rowData.id === 'range-indicator-bottom') {
                                const isTopIndicator = rowData.id === 'range-indicator-top';
                                const indicatorType = isTopIndicator ? topIndicatorType : bottomIndicatorType;
                                
                                return {
                                    sx: {
                                        height: 30,
                                        backgroundColor: indicatorType === 'red' ? 'var(--color-end-of-horizon-bg)' : indicatorType === 'orange' ? 'var(--color-filter-result-bg)' : 'transparent',
                                        fontSize: 12,
                                        fontWeight: 500,
                                        color: indicatorType === 'red' ? 'var(--color-end-of-horizon-text)' : indicatorType === 'orange' ? 'var(--color-filter-result-text)' : 'inherit',
                                        '& td': {
                                            textAlign: 'center',
                                            padding: '0px !important',
                                            border: 'none',
                                            backgroundColor: 'transparent'
                                        }
                                    },
                                    onClick: (e: any) => e.stopPropagation(),
                                };
                            }

                            // Handle current-day-placeholder
                            if (rowData.id === 'current-day-placeholder') {
                                return {
                                    sx: {
                                        height: 30,
                                        backgroundColor: '#ffe5b4c0',
                                        fontSize: 12,
                                        fontWeight: 500,
                                        color: '#cc6600',
                                        '& td': {
                                            textAlign: 'center',
                                            padding: '0px !important',
                                            border: 'none',
                                            backgroundColor: 'transparent'
                                        }
                                    },
                                    onClick: (e: any) => e.stopPropagation(),
                                };
                            }

                            // Handle no-data-placeholder
                            if (rowData.id === 'no-data-placeholder') {
                                return {
                                    sx: {
                                        height: 'calc(100vh - 400px)',
                                        backgroundColor: 'var(--color-page-background)',
                                        '& td': {
                                            padding: '0px !important',
                                            border: 'none',
                                            backgroundColor: 'var(--color-page-background)',
                                            verticalAlign: 'middle'
                                        }
                                    },
                                    onClick: (e: any) => e.stopPropagation(),
                                };
                            }

                            const rowDate = new Date(rowData.date);
                            rowDate.setHours(0, 0, 0, 0);
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const isCurrentDay = rowData.id !== 'current-day-placeholder' && rowDate.getTime() === today.getTime();

                            // Cashflow highlighting
                            const firstNegativeDateStr = companyStatus?.firstNegativeDate;
                            const firstNegativeGuid = companyStatus?.firstNegativeTransactionGuid;
                            const isCashflowRow = firstNegativeGuid
                                ? rowData.id === firstNegativeGuid
                                : (firstNegativeDateStr && new Date(rowData.date).toDateString() === new Date(firstNegativeDateStr).toDateString());
                            
                            // These four are mutually exclusive, most-specific-first: an
                            // unlinked provision falls into exactly one, each independently
                            // colorable on the Personalisation page (color-scheme-settings.tsx).
                            // Payroll is checked before recurring - a payroll template's own
                            // type is a payment cycle (e.g. 'weekly'), never 'one-time', so
                            // isRecurring resolves true for payroll rows too and would
                            // otherwise wrongly claim them.
                            // (A provision's date having been pushed forward or pulled back -
                            // see getProvisionDateShiftKind - no longer affects color, only
                            // the hourglass icon in the actions cell.)
                            const isInvoiceProvision = rowData.type === 'provision'
                                && !rowData.paidAtImport
                                && !!rowData.invoiceNumber;
                            const isPayrollProvision = rowData.type === 'provision'
                                && !rowData.paidAtImport
                                && !isInvoiceProvision
                                && !!rowData.staffPaymentId;
                            const isRecurringProvision = rowData.type === 'provision'
                                && !rowData.paidAtImport
                                && !isInvoiceProvision
                                && !isPayrollProvision
                                && rowData.isRecurring === true;
                            const isOneTimeProvision = rowData.type === 'provision'
                                && !rowData.paidAtImport
                                && !isInvoiceProvision
                                && !isPayrollProvision
                                && !isRecurringProvision;

                            let bgColor = 'var(--color-page-background)';
                            let textColor = 'var(--color-transaction-text)';

                            if (rowData.isSimulation) {
                                bgColor = 'var(--color-simulation-row-bg)';
                            } else if (isCurrentDay) {
                                bgColor = 'var(--color-current-day-bg)';
                            } else if (isCashflowRow) {
                                bgColor = 'var(--color-run-out-of-money-bg)';
                            } else if (isInvoiceProvision) {
                                bgColor = 'var(--color-invoice-provision-bg)';
                                textColor = getProvisionTextColor(rowData);
                            } else if (isPayrollProvision) {
                                bgColor = 'var(--color-payroll-provision-bg)';
                                textColor = getProvisionTextColor(rowData);
                            } else if (isRecurringProvision) {
                                bgColor = 'var(--color-recurring-provision-bg)';
                                textColor = getProvisionTextColor(rowData);
                            } else if (isOneTimeProvision) {
                                bgColor = 'var(--color-onetime-provision-bg)';
                                textColor = getProvisionTextColor(rowData);
                            } else if (rowData.type === 'real' || rowData.type === null || rowData.paidAtImport) {
                                bgColor = row.index % 2 === 0 ? 'var(--color-transaction-row-bg)' : 'rgba(var(--color-transaction-row-bg-rgb), 0.5)';
                            } else {
                                bgColor = row.index % 2 === 0 ? 'var(--color-provision-row-bg)' : 'rgba(var(--color-provision-row-bg-rgb), 0.5)';
                                textColor = getProvisionTextColor(rowData);
                            }

                            return {
                                onMouseEnter: () => setHoveredRowId(rowData.id),
                                onMouseLeave: () => setHoveredRowId(null),
                                onClick: () => {
                                    if (onRowClick) {
                                        onRowClick(rowData.id, rowData.date, rowData.bank, rowData.category, rowData.isSimulation);
                                    }
                                },
                                sx: {
                                    cursor: onRowClick ? 'pointer' : 'default',
                                    background: `${bgColor} !important`,
                                    color: `${textColor} !important`,
                                    fontWeight: isCashflowRow ? 700 : 400,
                                    transition: 'background 0.2s',
                                    ':hover': { background: rowData.id === 'current-day-placeholder' ? bgColor : isCashflowRow ? '#ff4d4d7d' : 'rgba(var(--color-primary-rgb), 0.2078)' },
                                },
                            };
                        }}
                    />
                    {isLoadingMore && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: '20px 10px',
                                marginTop: '10px'
                            }}
                        >
                            <CircularProgress size={24} sx={{ color: 'var(--color-primary)' }} />
                        </Box>
                    )}
                </Box>
            )}
            <ProvisionSelectorPopup
                open={showProvisionSelector}
                onClose={() => setShowProvisionSelector(false)}
                onSelect={(provisionId, /* description */) => {
                    const selectedGuids = Array.from(checkedRows);
                    if (onMarkTransactions) {
                        onMarkTransactions(selectedGuids, provisionId);
                    }
                    setCheckedRows(new Set());
                }}
            />
            <CategoryTaxConfirmPopup
                open={!!categoryChangeConfirm}
                currentTaxNames={(categoryChangeConfirm?.currentTaxIds ?? []).map(id => taxOptions.find(t => t.id === id)?.name).filter((name): name is string => !!name)}
                newTaxNames={(categoryDefaultTaxIds[String(categoryChangeConfirm?.newCategoryId ?? '')] ?? []).map(id => taxOptions.find(t => t.id === id)?.name).filter((name): name is string => !!name)}
                onCancel={() => setCategoryChangeConfirm(null)}
                onCategoryOnly={() => {
                    if (categoryChangeConfirm) {
                        onCategoryChange?.(categoryChangeConfirm.rowId, categoryChangeConfirm.newCategoryId, false);
                    }
                    setCategoryChangeConfirm(null);
                }}
                onCategoryAndTaxes={() => {
                    if (categoryChangeConfirm) {
                        onCategoryChange?.(categoryChangeConfirm.rowId, categoryChangeConfirm.newCategoryId, true);
                    }
                    setCategoryChangeConfirm(null);
                }}
            />
            <ProvisionTaxScopePopup
                open={!!taxScopeConfirm}
                onCancel={() => setTaxScopeConfirm(null)}
                onThisOccurrence={() => {
                    if (taxScopeConfirm) {
                        setTaxOverrides(prev => ({ ...prev, [taxScopeConfirm.rowId]: taxScopeConfirm.next }));
                        onTaxChange?.(taxScopeConfirm.rowId, taxScopeConfirm.next, 'this');
                    }
                    setTaxScopeConfirm(null);
                }}
                onAllOccurrences={() => {
                    if (taxScopeConfirm) {
                        setTaxOverrides(prev => ({ ...prev, [taxScopeConfirm.rowId]: taxScopeConfirm.next }));
                        onTaxChange?.(taxScopeConfirm.rowId, taxScopeConfirm.next, 'all');
                    }
                    setTaxScopeConfirm(null);
                }}
            />
            <DeletePopup
                open={!!taxChangeConfirm}
                message="Change Applied Taxes?"
                description="This will make the applied taxes different from this transaction's category default. Continue?"
                confirmLabel="Continue"
                cancelLabel="Cancel"
                onCancel={() => setTaxChangeConfirm(null)}
                onConfirm={() => {
                    if (taxChangeConfirm) {
                        setTaxOverrides(prev => ({ ...prev, [taxChangeConfirm.rowId]: taxChangeConfirm.next }));
                        onTaxChange?.(taxChangeConfirm.rowId, taxChangeConfirm.next);
                    }
                    setTaxChangeConfirm(null);
                }}
            />
        </Paper>
    );
});

TransactionsTableVirtual.displayName = 'TransactionsTableVirtual';

export default TransactionsTableVirtual;
